/**
 * UPG entity emoji glyphs: one distinct, relevant glyph per active entity type.
 *
 * The canonical, Captain-reviewed emoji set (icon-uniqueness pass). Single source
 * of truth, surfaced live via `get_type_label({type}).emoji` and
 * `list_type_labels()`, and consumed by the UPG site docs generator. Keep this
 * the ONLY place an entity emoji is authored, so renderers stop hardcoding.
 *
 * A type without an entry falls back to `DEFAULT_ENTITY_EMOJI` (the builder in
 * labels.ts applies the fallback), so the `emoji` field always resolves.
 *
 * https://unifiedproductgraph.org | MIT
 */

/** Fallback glyph for any active type without an explicit entry. */
export const DEFAULT_ENTITY_EMOJI = "📦" as const

/** Canonical emoji per entity type id (snake_case). */
export const ENTITY_EMOJI: Record<string, string> = {
  a11y_annotation: "🦻",
  a11y_audit: "♿",
  a11y_guideline: "🦯",
  a11y_issue: "🚫",
  a11y_standard: "📏",
  acceptance_criterion: "✅",
  access_policy: "🔑",
  account: "🏦",
  acquisition_channel: "🎣",
  ad_creative: "🖌️",
  affinity_cluster: "🧲",
  agent_definition: "🦾",
  agent_hook: "🧷",
  agent_session: "🧑‍💻",
  agent_skill: "🪄",
  agent_task: "🎫",
  aggregate: "🫙",
  ai_cost_tracker: "💸",
  ai_dataset: "🪆",
  ai_experiment: "👾",
  ai_guardrail: "🚦",
  ai_model: "🤖",
  ai_trace: "🪡",
  alert_rule: "🛎️",
  annotation: "🗯️",
  api_contract: "🤝",
  api_ecosystem: "🕸️",
  api_endpoint: "🔌",
  approval_record: "🔏",
  assumption: "🤔",
  attribution_model: "🕵️",
  audit_log_policy: "🪵",
  behavioral_segment: "🐾",
  beta_program: "🐣",
  bounded_context: "🔲",
  brand_asset: "🗂️",
  brand_colour: "🌈",
  brand_identity: "💠",
  brand_imagery: "🖼️",
  brand_logo: "🔷",
  brand_typography: "🔤",
  brand_voice: "🎙️",
  bug: "🐛",
  build_artifact: "🏺",
  business_model: "💰",
  capability: "💪",
  capacity_plan: "🗓️",
  ceremony: "🔔",
  certification: "🏵️",
  change_request: "✏️",
  changelog: "🕰️",
  churn_reason: "💔",
  ci_pipeline: "🚇",
  classification_axis: "↔️",
  classification_value: "📍",
  code_repository: "💾",
  cohort: "👥",
  command: "⌨️",
  community_initiative: "🌍",
  competitive_analysis: "📊",
  competitive_battle_card: "⚔️",
  competitor: "🥊",
  competitor_feature: "🔬",
  competitor_signal: "📡",
  compliance_framework: "🏟️",
  compliance_requirement: "⛲",
  capture: "📸",
  composition: "🖼️",
  constraint: "🔒",
  contact: "📇",
  content_calendar: "📅",
  content_piece: "🗒️",
  content_strategy: "✍️",
  content_theme: "💡",
  contract: "📑",
  contract_clause: "§",
  cost_structure: "🧮",
  cultural_adaptation: "🌏",
  customer_feedback: "🔶",
  customer_health_score: "💚",
  customer_journey_stage: "🚏",
  customer_relationship: "🫶",
  dashboard: "🎛️",
  data_classification: "🍃",
  data_contract: "🔣",
  data_domain: "🏷️",
  data_flow: "↕️",
  data_lineage: "🌳",
  data_model: "🗃️",
  data_pipeline: "🪣",
  data_product: "🎁",
  data_quality_rule: "🧹",
  data_source: "🛢️",
  database_schema: "🗄️",
  deal: "💼",
  decision: "⚖️",
  deliverable: "🎀",
  demand_gen_program: "📣",
  department: "🏬",
  dependency: "⛓️",
  deployment: "📤",
  design_component: "🧱",
  design_concept: "🌱",
  design_guideline: "📌",
  design_pattern: "🧩",
  design_question: "💭",
  design_sprint: "🏃",
  design_system: "🧰",
  design_token: "🪙",
  desired_outcome: "🌟",
  developer_portal: "🎑",
  discount_strategy: "✂️",
  distribution_channel: "🚚",
  document: "📃",
  documentation_template: "🖊️",
  domain_entity: "📦",
  domain_event: "📡",
  education_program: "🏫",
  email_sequence: "📧",
  epic: "📜",
  error_budget: "⏳",
  eval_benchmark: "🎖️",
  eval_run: "🎟️",
  event: "🎈",
  event_schema: "🔠",
  evidence: "🔎",
  experiment: "🧫",
  experiment_plan: "📐",
  experiment_run: "▶️",
  external_api: "🌐",
  feasibility_study: "🏗️",
  feature: "⭐",
  feature_area: "📁",
  feature_flag: "🎌",
  feature_request: "🙏",
  feedback_program: "🎤",
  feedback_theme: "📎",
  feedback_vote: "👍",
  fix: "🪛",
  forecast: "🔮",
  framework_exercise: "📋",
  funnel: "⬇️",
  funnel_step: "🔽",
  glossary_term: "📖",
  growth_campaign: "📢",
  growth_loop: "♾️",
  gtm_strategy: "🏹",
  hallucination_report: "👻",
  help_video: "🎬",
  hypothesis: "🧪",
  ideal_customer_profile: "👤",
  incident: "🚨",
  infrastructure_component: "🖧",
  initiative: "🚀",
  insight: "🔦",
  integration_partner: "🗿",
  integration_pattern: "🔗",
  interaction_spec: "🖱️",
  interview_guide: "📋",
  investigation: "🔍",
  invoice: "💳",
  ip_asset: "©️",
  job: "🔨",
  job_step: "👣",
  journey_action: "⚡",
  journey_phase: "🔖",
  journey_step: "🪜",
  key_activity: "🛠️",
  key_resource: "🏭",
  key_result: "📈",
  knowledge_base_article: "📓",
  launch: "🛸",
  lead: "🌿",
  learning: "🧠",
  learning_path: "🥾",
  legal_entity: "🏢",
  library_dependency: "📚",
  locale: "🌎",
  locale_config: "🗾",
  market_segment: "🎯",
  market_trend: "📉",
  marketing_campaign_plan: "📆",
  marketing_channel: "📻",
  marketing_strategy: "🔱",
  marketplace_listing: "🛒",
  messaging: "✉️",
  metric: "🔢",
  metric_quality_assessment: "🩺",
  milestone: "⛳",
  mission: "🧭",
  model_comparison: "🔄",
  monitor: "📺",
  need: "🫀",
  nps_campaign: "😊",
  objection: "🚧",
  objective: "🏁",
  observation: "👁️",
  on_call_rotation: "🏕️",
  opportunity: "🚪",
  organization: "🏙️",
  outcome: "🏆",
  participant: "🙋",
  partner_program: "🎗️",
  partner_revenue_share: "🤲",
  partner_tier: "🥈",
  partnership: "🫱🏻‍🫲🏼",
  paywall: "🛂",
  penetration_test: "🗡️",
  person: "🧑",
  persona: "🎭",
  pipeline_sales: "🪈",
  pipeline_stage: "🧽",
  planning_cycle: "🗓️",
  configuration_axis: "🎚️",
  playbook: "📒",
  portfolio: "🧸",
  positioning: "🎪",
  postmortem: "🩻",
  press_release: "🗞️",
  pricing_strategy: "💱",
  pricing_tier: "🥉",
  primitive: "📋",
  operating_lifecycle: "🔄",
  operating_stage: "🔹",
  privacy_policy: "🛡️",
  product: "🧬",
  product_area: "🪀",
  program: "🛋️",
  project: "🧳",
  prompt_template: "✒️",
  prompt_version: "🗨️",
  proof_point: "🧾",
  prototype: "📱",
  qa_session: "🐞",
  queue_topic: "📬",
  quote: "🗣️",
  quote_document: "💲",
  read_model: "🪟",
  rebuttal: "↩️",
  regional_pricing: "💴",
  regression_test: "♻️",
  release: "🚢",
  release_strategy: "🪂",
  report: "📰",
  research_plan: "🗺️",
  research_question: "❓",
  research_study: "🧑‍🔬",
  resource_allocation: "🥧",
  retrospective: "🪞",
  revenue_stream: "💵",
  review_gate: "🚥",
  risk: "🎲",
  risk_register: "🧯",
  roadmap: "🛣️",
  roadmap_item: "🚩",
  roadmap_theme: "🎨",
  role: "🪪",
  root_cause: "🪝",
  runbook: "📘",
  sales_motion: "🔁",
  screen: "🖥️",
  screen_state: "🔳",
  surface: "🪟",
  security_audit: "🛃",
  security_control: "🎚️",
  security_policy: "🔐",
  security_review: "🧐",
  seo_keyword: "#️⃣",
  service: "⚙️",
  service_blueprint: "🟣",
  service_level_agreement: "⏱️",
  service_level_indicator: "🌡️",
  service_level_objective: "🥅",
  skill: "🎓",
  social_post: "🐦",
  solution: "🔧",
  specification: "📋",
  stakeholder: "🧑‍💼",
  status_report: "📄",
  strategic_pillar: "🏛️",
  strategic_question: "❔",
  strategic_theme: "🧵",
  subscription: "🔂",
  success_milestone: "🎉",
  support_ticket: "🆘",
  survey_response: "📨",
  switching_cost: "⚓",
  symptom: "⚠️",
  task: "☑️",
  team: "🫂",
  team_okr: "🏅",
  technical_debt_item: "🪤",
  territory: "🏴",
  test_case: "🌵",
  test_coverage_report: "🎏",
  test_environment: "⚗️",
  test_plan: "📝",
  test_result: "🎰",
  test_suite: "📂",
  threat: "☠️",
  threat_model: "🏴‍☠️",
  touchpoint: "👆",
  translation_bundle: "🈂️",
  translation_key: "🗝️",
  trial_config: "🆓",
  tutorial: "🧑‍🏫",
  unit_economics: "💹",
  user_advisory_board: "🫵",
  user_flow: "🔀",
  user_journey: "🛤️",
  user_story: "💬",
  value_object: "💎",
  value_proposition: "✨",
  value_stream: "🌊",
  variant: "🆎",
  vision: "🔭",
  vulnerability: "🔓",
  walkthrough: "🚶",
  webinar: "🎥",
  wireframe: "▭",
  workflow_artifact: "🗳️",
  workflow_run: "⏯️",
  workflow_template: "🎻",
  workspace: "🏠",
}
